import { defineStore } from 'pinia'
import { logout as apiLogout, refreshTokenApi } from '@/api/auth'
import type { RoleInfo } from '@/api/auth'
import logger from '@/utils/logger'

export interface User {
  id: number
  username: string
  realName?: string
  email: string
  avatar?: string
  role: string  // 主角色 roleCode，如 'ADMIN', 'MOLD_MANAGER'（向后兼容保留）
  permissions?: string[]
  departmentId?: number
  departmentName?: string
  roleId?: number  // 主角色ID，如 1, 5（向后兼容保留）
  roleName?: string  // 主角色中文名，如 '管理员', '模管员'
  /**
   * 全部角色列表（多角色支持，后端 login 接口返回）
   * 含主角色与副角色，isPrimary 标识主角色。
   * 旧版本缓存的 user 无此字段，getter 会自动回退到主角色单值。
   */
  roles?: RoleInfo[]
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: User | null
  isLoggedIn: boolean
  refreshTokenExpired: number | null  // Refresh Token过期时间戳
}

/**
 * 归一化取用户全部角色 code。
 * 优先取 roles 数组（多角色）；旧缓存无 roles 时回退到主角色单值，保证老会话不失权。
 */
function normalizeRoleCodes(user: User | null): string[] {
  const list = user?.roles
  if (Array.isArray(list) && list.length > 0) {
    const codes = list
      .map((r) => r?.roleCode)
      .filter((c): c is string => typeof c === 'string' && c.length > 0)
    if (codes.length > 0) return codes
  }
  return user?.role ? [user.role] : []
}

/**
 * 归一化取用户全部角色 ID。规则同 normalizeRoleCodes。
 */
function normalizeRoleIds(user: User | null): number[] {
  const list = user?.roles
  if (Array.isArray(list) && list.length > 0) {
    const ids = list
      .map((r) => r?.id)
      .filter((i): i is number => typeof i === 'number')
    if (ids.length > 0) return ids
  }
  return typeof user?.roleId === 'number' ? [user.roleId] : []
}

/**
 * 判断用户是否拥有目标角色（OR 语义，命中任一角色即为 true）。
 * 同时支持角色代码（'ADMIN'）与角色ID（1 或 '1'）两种写法。
 */
function matchRole(user: User | null, target: string | number): boolean {
  if (target === null || target === undefined || target === '') return false
  const t = String(target)
  if (normalizeRoleCodes(user).some((c) => c === t)) return true
  if (normalizeRoleIds(user).some((id) => String(id) === t)) return true
  return false
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem('token') || null,
    refreshToken: localStorage.getItem('refreshToken') || null,
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    isLoggedIn: !!localStorage.getItem('token'),
    refreshTokenExpired: localStorage.getItem('refreshTokenExpired') 
      ? parseInt(localStorage.getItem('refreshTokenExpired')!) 
      : null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
    /** 主角色 code（仅用于展示/兼容旧逻辑；权限判断请勿使用，会漏掉副角色） */
    userRole: (state) => state.user?.role || '',
    /** 全部角色 code 列表（多角色，旧缓存自动回退主角色） */
    userRoles: (state): string[] => normalizeRoleCodes(state.user),
    /** 全部角色 ID 列表（多角色，旧缓存自动回退主角色） */
    userRoleIds: (state): number[] => normalizeRoleIds(state.user),
    /** 全部角色的中文名列表（用于界面展示，如"成型工 / 模管员"） */
    userRoleNames: (state): string[] => {
      const list = state.user?.roles
      if (Array.isArray(list) && list.length > 0) {
        const names = list
          .map((r) => r?.roleName)
          .filter((n): n is string => typeof n === 'string' && n.length > 0)
        if (names.length > 0) return names
      }
      return state.user?.roleName ? [state.user.roleName] : []
    },
    /** 是否为多角色账号（拥有 2 个及以上角色） */
    isMultiRole: (state): boolean => normalizeRoleCodes(state.user).length > 1,
    isRefreshTokenValid: (state) => {
      if (!state.refreshTokenExpired) return true
      return Date.now() < state.refreshTokenExpired
    },
    canRefreshToken: (state) => {
      // Refresh Token还没过期
      if (!state.refreshTokenExpired) return true
      return Date.now() < state.refreshTokenExpired
    },
    hasPermission: (state) => (permission: string) => {
      const permissions = state.user?.permissions || []
      return permissions.includes(permission)
    },
    /**
     * 角色判断（多角色 OR 语义）：主角色或任一副角色命中即为 true。
     * 兼容传入角色代码（'ADMIN'）或角色ID（1 / '1'）。
     */
    hasRole: (state) => (roleCode: string | number) => matchRole(state.user, roleCode),
    /**
     * 按角色ID判断（多角色 OR 语义）。
     */
    hasRoleById: (state) => (roleId: number) =>
      normalizeRoleIds(state.user).some((id) => id === roleId),
    /**
     * 是否拥有其中任一角色。
     * @example authStore.hasAnyRole(['ADMIN', 'MOLD_MANAGER'])
     */
    hasAnyRole: (state) => (roles: Array<string | number>) =>
      Array.isArray(roles) && roles.some((r) => matchRole(state.user, r)),
    /**
     * 是否同时拥有全部指定角色。
     * @example authStore.hasAllRoles(['FORMING_WORKER', 'MOLD_MANAGER'])
     */
    hasAllRoles: (state) => (roles: Array<string | number>) =>
      Array.isArray(roles) && roles.length > 0 && roles.every((r) => matchRole(state.user, r)),
    userInfo: (state) => state.user,
  },

  actions: {
    setToken(token: string | null) {
      this.token = token
      
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },

    setRefreshToken(refreshToken: string | null, expiresIn: number) {
      this.refreshToken = refreshToken
      
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
        // 计算Refresh Token过期时间（提前1小时标记为过期）
        const expireTime = Date.now() + (expiresIn * 1000) - (60 * 60 * 1000)
        this.refreshTokenExpired = expireTime
        localStorage.setItem('refreshTokenExpired', expireTime.toString())
      } else {
        localStorage.removeItem('refreshToken')
        this.refreshTokenExpired = null
        localStorage.removeItem('refreshTokenExpired')
      }
    },

    setUser(user: User | null) {
      this.user = user

      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      } else {
        localStorage.removeItem('user')
      }
    },

    /**
     * 刷新Access Token
     */
    async refreshAccessToken(): Promise<boolean> {
      if (!this.refreshToken || !this.canRefreshToken) {
        return false
      }

      try {
        const response = await refreshTokenApi(this.refreshToken)
        this.setToken(response.accessToken)
        this.setRefreshToken(response.refreshToken, parseInt(response.expiresIn))
        return true
      } catch (error) {
        logger.error('Token刷新失败:', error)
        return false
      }
    },

    /**
     * 登出
     */
    logout() {
      // 调用后端登出接口
      if (this.user && this.token) {
        apiLogout(this.user.id, this.token, this.refreshToken || undefined).catch(() => {
          // 忽略登出接口错误
        })
      }
      
      this.clearAuth()
    },

    /**
     * 清除认证信息
     */
    clearAuth() {
      this.token = null
      this.refreshToken = null
      this.user = null
      this.isLoggedIn = false
      this.refreshTokenExpired = null
      
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('refreshTokenExpired')
      localStorage.removeItem('user')
    },
  },
})
